import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../css/Account.css';
import { motion } from 'framer-motion';
import { domain } from './Domain';
import useFetch from './useFetch';
import useApiRequest from './useApiRequest';
import Skeleton from 'react-loading-skeleton';
import { useLoginContext } from './LoginContext';

const Disclaimer = () => (
    <div className="profile-disclaimer">
        <h3 className="profile-disclaimer-title">Security notice</h3>
        <p className="profile-disclaimer-copy mb-0">
            Keep your account details private. Use strong passwords and rotate them
            regularly to reduce the risk of unauthorized access.
        </p>
    </div>
);

const EMPTY_PROFILE = {
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    dob: '',
};

const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(d);
};

const Profile = () => {
    const { userInfo, setUserInfo } = useLoginContext();
    const imageInputRef = useRef(null);
    const { putRequest } = useApiRequest();
    const { data, loading, error, refresh, setRefresh } = useFetch(
        `${domain}/accounts/profile`
    );
    const [formData, setFormData] = useState(EMPTY_PROFILE);
    const [profileFile, setProfileFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!data) return;
        setFormData({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            contact: data.contact || '',
            dob: data.dob || '',
        });
    }, [data]);

    const fullName = useMemo(() => {
        if (!data) return '-';
        if (data.regCategory === 'TrainingCenter') return data.name || '-';
        return `${data.first_name || ''} ${data.last_name || ''}`.trim() || '-';
    }, [data]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePut = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = new FormData();
            payload.append('first_name', formData.first_name);
            payload.append('last_name', formData.last_name);
            payload.append('email', formData.email);
            payload.append('contact', formData.contact);
            if (data?.regCategory !== 'TrainingCenter') {
                payload.append('dob', formData.dob || '');
            }
            if (profileFile) {
                payload.append('profile', profileFile);
            }

            const res = await putRequest(`${domain}/accounts/profile`, payload);
            setRefresh(!refresh);
            if (res?.message === 'user update successfull') {
                setUserInfo((prev) => ({
                    ...prev,
                    image: res?.data?.profile || prev.image,
                }));
            }
            setProfileFile(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.section className="profile-layout">
            <motion.article className="profile-card profile-card--form">
                <header className="profile-card-header">
                    <h2 className="profile-card-title">Edit account information</h2>
                    <p className="profile-card-subtitle mb-0">
                        Update your public and contact details.
                    </p>
                </header>

                {loading && (
                    <div className="profile-skeleton-stack">
                        <Skeleton height={34} />
                        <Skeleton height={34} />
                        <Skeleton height={34} />
                        <Skeleton height={34} />
                    </div>
                )}

                {error && !loading && (
                    <div className="alert alert-danger" role="alert">
                        Failed to load profile: {error}
                    </div>
                )}

                {!loading && data && (
                    <form onSubmit={handlePut} className="profile-form">
                        <div className="profile-grid-two">
                            <div>
                                <label htmlFor="profile-first-name">First name</label>
                                <input
                                    id="profile-first-name"
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    placeholder="First name"
                                />
                            </div>
                            <div>
                                <label htmlFor="profile-last-name">Last name</label>
                                <input
                                    id="profile-last-name"
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    placeholder="Last name"
                                />
                            </div>
                        </div>

                        <label htmlFor="profile-email">Email</label>
                        <input
                            id="profile-email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email"
                        />

                        <label htmlFor="profile-contact">Phone number</label>
                        <input
                            id="profile-contact"
                            type="tel"
                            name="contact"
                            value={formData.contact}
                            onChange={handleChange}
                            placeholder="Phone number"
                        />

                        {data.regCategory !== 'TrainingCenter' && (
                            <>
                                <label htmlFor="profile-dob">Date of birth</label>
                                <input
                                    id="profile-dob"
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                />
                            </>
                        )}

                        <div className="profile-upload-row">
                            <input
                                type="file"
                                ref={imageInputRef}
                                name="profile"
                                accept="image/*"
                                className="d-none"
                                onChange={(e) =>
                                    setProfileFile(e.target.files?.[0] || null)
                                }
                            />
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => imageInputRef.current?.click()}
                            >
                                {profileFile
                                    ? 'Change selected photo'
                                    : 'Select new profile photo'}
                            </button>
                            {profileFile && (
                                <small className="text-muted">
                                    {profileFile.name}
                                </small>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary profile-save-btn"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Update information'}
                        </button>
                    </form>
                )}
            </motion.article>

            <motion.article className="profile-card profile-card--summary">
                <header className="profile-card-header">
                    <h2 className="profile-card-title">Saved profile information</h2>
                    <p className="profile-card-subtitle mb-0">
                        Snapshot of your current account details.
                    </p>
                </header>

                <div className="profile-summary">
                    <div className="profile-avatar-wrap">
                        {loading ? (
                            <Skeleton circle height={92} width={92} />
                        ) : (
                            <img
                                className="profile-avatar"
                                src={data ? domain + data.profile : '/images/user.png'}
                                alt={fullName}
                            />
                        )}
                    </div>

                    <div className="profile-facts">
                        <div>
                            <p className="profile-facts-label">Name</p>
                            <p className="profile-facts-value">{fullName}</p>
                        </div>
                        <div>
                            <p className="profile-facts-label">Contact</p>
                            <p className="profile-facts-value">{data?.contact || '-'}</p>
                        </div>
                        {data?.regCategory !== 'TrainingCenter' && (
                            <div>
                                <p className="profile-facts-label">Gender</p>
                                <p className="profile-facts-value">{data?.gender || '-'}</p>
                            </div>
                        )}
                        <div>
                            <p className="profile-facts-label">Email</p>
                            <p className="profile-facts-value">{data?.email || '-'}</p>
                        </div>
                        <div>
                            <p className="profile-facts-label">Joining date</p>
                            <p className="profile-facts-value">
                                {formatDate(data?.dateJoined)}
                            </p>
                        </div>
                    </div>
                </div>

                <Disclaimer />
            </motion.article>
        </motion.section>
    );
};

export default Profile;